import React from "react";
import {
  Stack,
  Box,
  Typography,
  Paper,
  Skeleton,
  Tooltip,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import WarningIcon from "@mui/icons-material/Warning";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

import { MetricCard } from "../shared";
import InfoButton from "../Clinical/InfoButton";
import {
  HEALTH_STATUS_COLORS,
  HEALTH_STATUS_ORDER,
  formatNumber,
} from "../../constants/ceoDashboard";

/**
 * Panel 1 - Population Overview (v2)
 * Muestra 5 KPI cards principales + barra horizontal de distribución por Health Status.
 *
 * Props:
 * - data: objeto de population/overview.json
 * - loading: boolean
 * - helpContent: contenido para el InfoButton
 * - onOpenNewMembers: callback para abrir el diálogo de nuevos miembros
 */
export default function PanelPopulationOverview({
  data,
  loading,
  helpContent,
  onOpenNewMembers,
}) {
  // ── Loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <Box>
        {/* Skeleton header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Skeleton variant="rounded" width={36} height={36} animation="wave" />
          <Skeleton variant="text" width={200} height={32} animation="wave" />
        </Stack>

        {/* Skeleton KPI cards */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            mb: 2,
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <MetricCard key={i} loading />
          ))}
        </Box>

        {/* Skeleton distribution bar */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Skeleton variant="text" width={200} animation="wave" sx={{ mb: 1 }} />
          <Skeleton
            variant="rectangular"
            height={32}
            animation="wave"
            sx={{ borderRadius: 2 }}
          />
          <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="text" width={80} animation="wave" />
            ))}
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (!data) return null;

  const total = data.totalMembers || 1;
  const establishedPct =
    data.establishedPatients && data.totalMembers
      ? Math.round((data.establishedPatients / data.totalMembers) * 100)
      : 0;

  return (
    <Box>
      {/* ── Header ──────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <PeopleIcon fontSize="small" />
        </Box>
        <Typography variant="h6" fontWeight={700}>
          Population Overview
        </Typography>
        <InfoButton helpContent={helpContent} />
      </Stack>

      {/* ── KPI Cards (5 cols) ──────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          },
          mb: 2,
        }}
      >
        <MetricCard
          title="Total Members"
          value={formatNumber(data.totalMembers)}
          icon={<PeopleIcon />}
          color="primary"
        />

        <MetricCard
          title="Established Patients"
          value={formatNumber(data.establishedPatients)}
          subtitle={`${establishedPct}% of total`}
          icon={<MedicalServicesIcon />}
          color="info"
        />

        <MetricCard
          title="New Members"
          value={formatNumber(data.newMembersCount)}
          subtitle="Click to view details"
          icon={<PersonAddIcon />}
          color="success"
          onClick={onOpenNewMembers}
        />

        <MetricCard
          title="High Risk"
          value={`${data.highRiskPercent}%`}
          subtitle={`${formatNumber(data.highRiskCount)} patients`}
          icon={<WarningIcon />}
          color={data.highRiskPercent > 10 ? "error" : "warning"}
          trendInverted
        />

        <MetricCard
          title="ER Rate"
          value={`${data.erRate}%`}
          subtitle={`${formatNumber(data.erWithEvents)} with ER events`}
          icon={<LocalHospitalIcon />}
          color={data.erRate > 10 ? "error" : "warning"}
          trendInverted
        />
      </Box>

      {/* ── Health Distribution Bar ─────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Health Status Distribution
        </Typography>

        {/* Stacked bar */}
        <Box
          sx={{
            display: "flex",
            height: 32,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {HEALTH_STATUS_ORDER.map((status) => {
            const count = data.healthStatusDistribution?.[status] || 0;
            const pct = (count / total) * 100;
            if (count === 0) return null;

            return (
              <Tooltip
                key={status}
                title={`${status}: ${formatNumber(count)} (${pct.toFixed(1)}%)`}
                arrow
              >
                <Box
                  sx={{
                    width: `${pct}%`,
                    bgcolor: HEALTH_STATUS_COLORS[status],
                    minWidth: pct > 1 ? 8 : 2,
                    transition: "opacity 200ms ease",
                    "&:hover": { opacity: 0.8 },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>

        {/* Legend */}
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={2}
          sx={{ mt: 1.5 }}
        >
          {HEALTH_STATUS_ORDER.map((status) => {
            const count = data.healthStatusDistribution?.[status] || 0;
            if (count === 0) return null;

            return (
              <Stack
                key={status}
                direction="row"
                alignItems="center"
                spacing={0.75}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: HEALTH_STATUS_COLORS[status],
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {status}
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {formatNumber(count)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Paper>
    </Box>
  );
}
